import {
  Divider,
  Grid,
  GridItem,
  RadioCard,
  RadioGroupField,
  Text,
} from "@certn/cascadia-react";
import { components } from "@certn/legacy-api/generated-schemas/people";
import { Field, useField } from "formik";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  AddressInput,
  initialValues,
} from "../../../../components/AddressInput";
import { useUser } from "../../../../hooks/api/useUser";
import { useApplicationCurrentAddress } from "../../hooks";

type PositionOrPropertyLocationCheckField =
  components["schemas"]["PositionOrPropertyLocationCheckField"];

export enum LocationChoices {
  NO = "no",
  REMOTE = "remote",
  LOCATION = "location",
  NONE = "",
}

export function PositionLocationInputField({
  field: config,
}: {
  field: PositionOrPropertyLocationCheckField;
}) {
  const { t } = useTranslation();
  const [option, setOption] = useState<string>("");
  const { user } = useUser();

  const currentAddress = useApplicationCurrentAddress();

  const [field, meta, positionLocationhelpers] = useField(config.name);

  // if user address matches address on application, then it is likely "Remote" location
  const userAddress =
    currentAddress || user?.personal_profile?.profile_addresses?.[0];
  const isRemote = field.value && field.value.address === userAddress?.address;

  useEffect(() => {
    if (field.value?.location_type === "Gig Location") {
      setOption(LocationChoices.NO);
    } else if (isRemote) {
      setOption(LocationChoices.REMOTE);
    } else if (!field.value?.location_type) {
      setOption(LocationChoices.NONE);
    } else if (field.value) {
      setOption(LocationChoices.LOCATION);
    }
  }, [field.value]);

  const handleChange = (value: string) => {
    setOption(value);

    if (value === LocationChoices.LOCATION) {
      positionLocationhelpers.setValue({
        location_type: "Position Location",
        address: isRemote ? "" : field.value?.address ?? "",
        country: isRemote
          ? initialValues.country
          : field.value?.country ?? initialValues.country,
        county: isRemote
          ? initialValues.county
          : field.value?.county ?? initialValues.county,
        city: isRemote ? "" : field.value?.city ?? "",
        province_state: isRemote ? "" : field.value?.province_state ?? "",
        postal_code: isRemote ? "" : field.value?.postal_code ?? "",
      });
    } else if (value === LocationChoices.REMOTE) {
      if (!userAddress) {
        console.error("Missing user address");
      }

      positionLocationhelpers.setValue({
        location_type: "Position Location",
        address: userAddress?.address,
        country: userAddress?.country,
        county: userAddress?.county ?? "",
        city: userAddress?.city,
        province_state: userAddress?.province_state,
        postal_code: userAddress?.postal_code,
      });
    } else if (value === LocationChoices.NO) {
      positionLocationhelpers.setValue({ location_type: "Gig Location" });
    }
  };

  return (
    <>
      <GridItem>
        <Field
          direction="column"
          as={RadioGroupField}
          name={`${field.name}.choice`}
          value={option}
          isRequired={config.required}
          aria-label={config.name + " radio button selections"}
          onChange={handleChange}
          testID="position-location-radio-group"
          errorMessage={
            meta.touched &&
            (meta.error as unknown as Record<string, string> | null)?.choice
          }
        >
          <RadioCard
            testID="position-location-no-specific-address-radio"
            value={LocationChoices.NO}
            description={t(
              "This position is a delivery, ride share, trades or similar service job with no fixed address."
            )}
          >
            {t("No specific address")}
          </RadioCard>
          <RadioCard
            testID="position-location-remotely-radio"
            value={LocationChoices.REMOTE}
            description={t("This position is primarily in my house.")}
          >
            {t("Remotely")}
          </RadioCard>
          <RadioCard
            testID="position-location-set-address-radio"
            value={LocationChoices.LOCATION}
            description={t(
              "This position is outside of my house for a business with a specific address."
            )}
          >
            {t("Set office or work address")}
          </RadioCard>
        </Field>
      </GridItem>
      {option === LocationChoices.LOCATION && (
        <>
          <GridItem>
            <Divider />
          </GridItem>
          <GridItem>
            <Text as="h2" style="form/section-heading">
              {t("Office or work address")}
            </Text>
          </GridItem>
          <GridItem>
            <Grid width="100%">
              <AddressInput isWithoutUnit nestedFieldName={config.name} />
            </Grid>
          </GridItem>
        </>
      )}
    </>
  );
}
